import React, {useCallback, useMemo} from 'react'

import {FloatingFocusManager, FloatingOverlay, FloatingPortal, useDismiss, useFloating, useInteractions, useRole} from '@floating-ui/react'
import {AnimatePresence, motion} from 'framer-motion'
import {isFunction} from 'lodash'
import {DialogContextProvider} from './DialogContext'
import useIsMounted from '../../hooks/useIsMounted'

const MotionOverlay = motion(FloatingOverlay)

export type DialogProps = {
  open: boolean
  setOpen: (open: boolean) => void
  closeOnEscape?: boolean
  closeOnOutsideClick?: boolean
  children:
    | JSX.Element
    | React.FC<{
        close: () => void
      }>
    | null
}

const Dialog: React.FC<DialogProps> = ({children, open, setOpen, closeOnEscape = true, closeOnOutsideClick = true}) => {
  const isMounted = useIsMounted()

  const close = useCallback(() => {
    if (isMounted()) setOpen(false)
  }, [isMounted, setOpen])

  const {refs, context} = useFloating({
    open,
    onOpenChange: setOpen,
  })

  const dismiss = useDismiss(context, {
    outsidePress: closeOnOutsideClick,
    outsidePressEvent: 'mousedown',
    escapeKey: closeOnEscape,
  })
  const role = useRole(context)

  const {getFloatingProps} = useInteractions([dismiss, role])

  const dialogContent = useMemo(() => {
    if (isFunction(children)) {
      return children({close}) ?? <></>
    }

    return children ?? <></>
  }, [children, close])

  return (
    <DialogContextProvider refs={refs} context={context} getFloatingProps={getFloatingProps} close={close}>
      <AnimatePresence>
        {open && (
          <FloatingPortal>
            <MotionOverlay
              style={{
                zIndex: 100,
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(202, 202, 202, 0.4)',
                backdropFilter: 'blur(2px)',
                padding: '32px 32px',
                boxSizing: 'border-box',
              }}
              lockScroll
              initial={{
                backdropFilter: 'blur(0px)',
                background: 'rgba(202, 202, 202, 0)',
              }}
              animate={{
                backdropFilter: 'blur(2px)',
                background: 'rgba(202, 202, 202, 0.4)',
              }}
              exit={{
                backdropFilter: 'blur(0px)',
                background: 'rgba(202, 202, 202, 0)',
              }}
            >
              <FloatingFocusManager context={context}>{dialogContent}</FloatingFocusManager>
            </MotionOverlay>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </DialogContextProvider>
  )
}

export default Dialog
